import './spinner.directive.ajscss';

import { isDefined } from 'angular';

import templateUrl from './spinner.directive.ajs.html';

export let SpinnerDirectiveAjs = () => {
  let setScopeValues = scope => {
    scope.ngModel = isDefined(scope.ngModel) ? scope.ngModel : 0;
    scope.max = isDefined(scope.max) ? scope.max : null;
    scope.min = isDefined(scope.min) ? scope.min : null;
    if (scope.max !== null && scope.min! == null && scope.max < scope.min) {
      scope.max = null;
      scope.min = null;
    }
    scope.step = isDefined(scope.step) ? Math.abs(parseInt(scope.step, 10)) : 1;
    scope.localValue = scope.ngModel.toString();
  };

  /**
   * A function to set caret position, this will be used when a special function who manipulate
   * srcElement of an event
   * @param scope
   * @param attrs
   */
  let setCaretPosition = (elem, caretPos) => {
    if (elem !== null) {
      if (elem.createTextRange) {
        let range = elem.createTextRange();
        range.move('character', caretPos);
        range.select();
      } else {
        if (elem.selectionStart) {
          elem.focus();
          elem.setSelectionRange(caretPos, caretPos);
        } else {
          elem.focus();
        }
      }
    }
  };

  return {
    restrict: 'E',
    require: 'ngModel',
    transclude: false,
    scope: {
      ngModel: '=',
      min: '<',
      max: '<',
      step: '<',
      notifyChange: '&', // While it's two-way data binding, we insist in informing the change on ngModel from children side to parent
    },
    templateUrl,
    link: (scope, elem) => {
      setScopeValues(scope);

      /**
       * All changes of model will be reflected on view display
       */
      scope.$watch('ngModel', newValue => {
        scope.localValue = newValue.toString();
      });

      scope.$watch('min+max', () => {
        let valueAllowed = getValidNumber(scope.ngModel);
        if (valueAllowed !== scope.ngModel) {
          scope.ngModel = valueAllowed;
          scope.notifyChange({ newVal: valueAllowed });
        }
      });

      elem.on('keydown', e => {
        let key = e.keyCode;
        let srcField = e.originalEvent.srcElement;
        switch (key) {
          // functional keys should be allowed to affect the value of input
          case 8:
          case 9:
          case 13:
          case 16:
          case 17:
          case 18:
          case 27:
          case 37:
          case 39:
            break;
          case 38: // UP
            scope.increment();
            break;
          case 40: // DOWN
            scope.decrement();
            break;
          default:
            if (key >= 48 && key <= 57) {
              // when number on functional-keyboard is input, directly update input field or intercept it
              if (srcField !== null && typeof srcField !== 'undefined') {
                onInputNumberOnUSKeyboard(srcField, key - 48, e);
              } else {
                e.preventDefault();
              }
            } else if (key >= 96 && key <= 105) {
              // when number on number-keyboard is input, directly update input field
              return;
            } else {
              // The non-number and non-functional input should be intercepted
              e.preventDefault();
            }
            break;
        }
      });

      elem.on('mousewheel', e => {
        if (e.originalEvent.wheelDelta >= 0) {
          scope.increment();
        } else {
          scope.decrement();
        }
        e.preventDefault();
      });

      /**
       * This function is specially designed for number input from USA keyboard(or in case no
       * number-keyboard is offered): to avoid the forbidden character to be entered (this will be the case when we
       * enter from FR functional-keyboard), we force a transformation to assure all of the enter event from
       * functional-keyboard is leading to a number input
       * @param srcField
       * @param inputNumber
       */
      let onInputNumberOnUSKeyboard = (srcField, inputNumber, event) => {
        event.preventDefault();
        let caretPos = srcField.selectionStart;
        let startString = srcField.value.slice(0, srcField.selectionStart);
        let endString = srcField.value.slice(srcField.selectionEnd, srcField.value.length);
        let newString = startString + inputNumber + endString || '0';
        updateValue(parseInt(newString, 10));
        setCaretPosition(srcField, caretPos + 1);
      };

      /**
       * The function called when we want to change input value by JS code
       * @param value
       */
      let updateValue = newNumber => {
        let newVal = getValidNumber(newNumber);
        scope.ngModel = newVal;
        scope.notifyChange({ newVal: newVal });
      };

      /**
       * The function called when local value was changed from input(without validity check neither notification to parent)
       */
      scope.localValueChanged = () => {
        let valueAllowed;
        if (scope.localValue === '') {
          valueAllowed = scope.min !== null ? scope.min : 0;
        } else {
          valueAllowed = getValidNumber(parseInt(scope.localValue, 10));
        }
        scope.ngModel = valueAllowed;
        scope.notifyChange({ newVal: valueAllowed });
      };

      /**
       * The function called to obtain a valid number considering min or max limits
       * @param toBeChecked
       *  number to be checked
       */
      let getValidNumber = (toBeChecked: number): number => {
        if (isOverMax(toBeChecked, true)) {
          return scope.max;
        }
        if (isOverMin(toBeChecked, true)) {
          return scope.min;
        }
        return toBeChecked;
      };

      /**
       * To respect max limit
       * @param toBeCompared
       * @param strict
       */
      let isOverMin = (toBeCompared, strict) => {
        let offset = strict ? 0 : 1;
        return isDefined(scope.min) && toBeCompared - offset < parseInt(scope.min, 10);
      };

      /**
       * To respect min limit
       * @param toBeCompared
       * @param strict
       */
      let isOverMax = (toBeCompared, strict) => {
        let offset = strict ? 0 : 1;
        return isDefined(scope.max) && toBeCompared + offset > parseInt(scope.max, 10);
      };

      /**
       * Increment by step
       */
      scope.increment = () => {
        updateValue(scope.ngModel + scope.step);
      };

      /**
       * Decrement by step
       */
      scope.decrement = () => {
        updateValue(scope.ngModel - scope.step);
      };

      // fix to prevent memory leak
      scope.$on('$destroy', () => {
        // remove listener
        elem.off('keydown');
        elem.off('mousewheel');
      });

      elem.on('$destroy', () => {
        scope.$destroy();
      });
    },
  };
};

export default 'cngSpinner';
